import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combine } from 'src/app/shared/utilities/route';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { Observable, filter, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { FacilityNote } from '../models/facility-note.model';
import {
    selectConfirmationDialogData,
    selectConfirmationDialogResult
} from '../selectors/confirmation-dialog.selectors';
import { showToasterMessage } from '../actions/toaster.actions';
import { ConfirmationDialogSettings } from '../models/confirmation-dialog-settings.model';
import { openConfirmationDialog } from '../actions/confirmation-dialog.actions';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import {
    AccountManagerDashboardFollowupItem,
    UserFollowupTimeBuckets
} from 'src/app/accountmanagers/dashboard/models/account-manager-dashboard-tile-followups.model';
import { AccountManagerDashboardFollowupType } from 'src/app/accountmanagers/dashboard/enums/account-manager-dashboard.enum';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class FacilityFollowupsService {
    facilityFollowupIdToMarkAsComplete: number;
    facilitySystemFollowupIdToMarkAsComplete: number;

    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        private readonly _store: Store<unknown>,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    public markFacilityFollowupComplete(facilityFollowupId: number): Observable<number> {
        const url = `${this.coreApiPath}/facility-system-followups/facility/${facilityFollowupId}/mark-complete`;
        return this.http.put<number>(url, '');
    }

    public markFacilitySystemFollowupComplete(facilitySystemFollowupId: number): Observable<number> {
        const url = `${this.coreApiPath}/facility-system-followups/system/${facilitySystemFollowupId}/mark-complete`;
        return this.http.put<number>(url, '');
    }

    public getAccountManagerDashboardFollowups(
        followupType: AccountManagerDashboardFollowupType,
        utcOffset: number
    ): Observable<AccountManagerDashboardFollowupItem[]> {
        const url = `${this.coreApiPath}/facility-system-followups/account-manager-dashboard-followups?followupType=${followupType}&utcOffset=${utcOffset}`;
        return this.http.get<AccountManagerDashboardFollowupItem[]>(url);
    }

    getUserFollowupTimeBuckets(utcOffset: number): Observable<UserFollowupTimeBuckets> {
        const url = `${this.coreApiPath}/facility-system-followups/user-time-frame-buckets/${utcOffset}`;
        return this.http.get<UserFollowupTimeBuckets>(url);
    }

    appendFollowupDateToNote(facilityNotes: FacilityNote[]): FacilityNote[] {
        return facilityNotes.map((note) => {
            let followupDateLocalString = '';
            if (note.facilityFollowup?.followupDateUtc) {
                followupDateLocalString = `@ ${dayjs.utc(note.facilityFollowup.followupDateUtc).local().format('MM/DD/YYYY hh:mm A')}`;
            } else if (note.facilitySystemFollowup?.followupDateUtc) {
                followupDateLocalString = `@ ${dayjs.utc(note.facilitySystemFollowup.followupDateUtc).local().format('MM/DD/YYYY hh:mm A')}`;
            }
            return { ...note, note: `${note.note} ${followupDateLocalString}` };
        });
    }

    openMarkAsCompleteDialog(facilityFollowupId: number, facilitySystemFollowupId: number): void {
        this.facilityFollowupIdToMarkAsComplete = facilityFollowupId;
        this.facilitySystemFollowupIdToMarkAsComplete = facilitySystemFollowupId;

        if (this.facilityFollowupIdToMarkAsComplete || this.facilitySystemFollowupIdToMarkAsComplete) {
            const settings = {
                title: 'Mark follow-up as complete?',
                message:
                    'Are you sure you want to mark this follow-up as complete? Completed follow-ups can not be undone and a new follow-up will need to be created if needed.',
                action: 'MARK AS COMPLETE',
                cancelText: 'CANCEL',
                panelClass: ['nova__dialog', 'nova__dialog--md'],
                disableClose: false,
                addtlData: {
                    facilityFollowupIdToMarkAsComplete: this.facilityFollowupIdToMarkAsComplete,
                    facilitySystemFollowupIdToMarkAsComplete: this.facilitySystemFollowupIdToMarkAsComplete
                }
            } as ConfirmationDialogSettings;

            this._store.dispatch(openConfirmationDialog({ settings: settings }));
        }
    }

    handleMarkAsCompleteConfirmationResult(): Observable<number> {
        return this._store.select(selectConfirmationDialogResult).pipe(
            filter((result) => result),
            withLatestFrom(this._store.select(selectConfirmationDialogData)),
            untilDestroyed(this),
            switchMap(
                ([confirmed, data]: [
                    boolean,
                    {
                        facilityFollowupIdToMarkAsComplete: number;
                        facilitySystemFollowupIdToMarkAsComplete: number;
                    }
                ]) => {
                    if (confirmed) {
                        if (data.facilityFollowupIdToMarkAsComplete) {
                            return this.updateFacilityFollowupStatus(this.facilityFollowupIdToMarkAsComplete);
                        } else if (data.facilitySystemFollowupIdToMarkAsComplete) {
                            return this.updateSystemFollowupStatus(this.facilitySystemFollowupIdToMarkAsComplete);
                        }
                    }
                    return of(0);
                }
            )
        );
    }

    updateFacilityFollowupStatus(facilityFollowupId: number): Observable<number> {
        return this.markFacilityFollowupComplete(facilityFollowupId).pipe(
            untilDestroyed(this),
            tap((result) => {
                if (result > 0) {
                    let msg = `${result} facility follow-ups have been completed.`;
                    if (result === 1) {
                        msg = '1 facility follow-up had been completed.';
                    }
                    this._store.dispatch(
                        showToasterMessage({
                            message: msg
                        })
                    );
                }
            })
        );
    }

    updateSystemFollowupStatus(facilitySystemFollowupId: number): Observable<number> {
        return this.markFacilitySystemFollowupComplete(facilitySystemFollowupId).pipe(
            untilDestroyed(this),
            tap((result) => {
                if (result > 0) {
                    let msg = `${result} system follow-ups have been completed.`;
                    if (result === 1) {
                        msg = '1 system follow-up had been completed.';
                    }
                    this._store.dispatch(
                        showToasterMessage({
                            message: msg
                        })
                    );
                }
            })
        );
    }
}
